import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function FloatingParticles() {
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 3 + 1,
    delay: Math.random() * 5,
    duration: Math.random() * 20 + 20,
    opacity: Math.random() * 0.5 + 0.1,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full bg-teal-300 animate-particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            opacity: p.opacity,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

function AnimatedWaveform() {
  const bars = Array.from({ length: 32 }, (_, i) => ({
    id: i,
    delay: i * 0.05,
    height: Math.random() * 60 + 20,
  }));

  return (
    <div className="flex items-center justify-center gap-[2px] h-16">
      {bars.map((bar) => (
        <div
          key={bar.id}
          className="w-[3px] rounded-full bg-gradient-to-t from-teal-400 to-emerald-300 animate-waveform"
          style={{
            animationDelay: `${bar.delay}s`,
            height: `${bar.height}%`,
          }}
        />
      ))}
    </div>
  );
}

function NotificationCard({
  icon,
  iconBg,
  title,
  subtitle,
  delay,
}: {
  icon: string;
  iconBg: string;
  title: string;
  subtitle: string;
  delay: number;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`flex items-center gap-3 bg-white/[0.07] backdrop-blur-xl rounded-xl p-3 border border-white/10 transition-all duration-700 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
      }`}
    >
      <div className={`w-9 h-9 ${iconBg} rounded-lg flex items-center justify-center shrink-0`}>
        <i className={`${icon} text-white text-base`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-white text-xs font-semibold truncate">{title}</p>
        <p className="text-white/50 text-[10px]">{subtitle}</p>
      </div>
    </div>
  );
}

function StatsCounter({ end, label, suffix = '' }: { end: number; label: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const counted = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !counted.current) {
          counted.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = end / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return (
    <div ref={ref} className="text-center">
      <div className="text-2xl font-bold text-white">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-xs text-white/50 mt-1">{label}</div>
    </div>
  );
}

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#060B18]">
      {/* Video Modal */}
      {showVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm cursor-pointer"
          onClick={() => setShowVideo(false)}
        >
          <div
            className="relative w-full max-w-4xl mx-4 aspect-video rounded-2xl overflow-hidden shadow-2xl shadow-black/60"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              title="vimeo-player"
              src="https://player.vimeo.com/video/1156355064?h=3ba8289792&autoplay=1"
              className="w-full h-full"
              frameBorder="0"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              allowFullScreen
            />
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a
              href="#features"
              className={`text-sm font-medium transition-colors cursor-pointer hover:text-teal-500 ${
                scrolled ? 'text-gray-700' : 'text-white/80'
              }`}
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className={`text-sm font-medium transition-colors cursor-pointer hover:text-teal-500 ${
                scrolled ? 'text-gray-700' : 'text-white/80'
              }`}
            >
              How It Works
            </a>
            <a
              href="#pricing"
              className={`text-sm font-medium transition-colors cursor-pointer hover:text-teal-500 ${
                scrolled ? 'text-gray-700' : 'text-white/80'
              }`}
            >
              Pricing
            </a>
            <a
              href="#testimonials"
              className={`text-sm font-medium transition-colors cursor-pointer hover:text-teal-500 ${
                scrolled ? 'text-gray-700' : 'text-white/80'
              }`}
            >
              Testimonials
            </a>
            <a
              href="#faq"
              className={`text-sm font-medium transition-colors cursor-pointer hover:text-teal-500 ${
                scrolled ? 'text-gray-700' : 'text-white/80'
              }`}
            >
              FAQ
            </a>
            <Link
              to="/start-free-trial"
              className="px-6 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold rounded-full hover:shadow-lg hover:shadow-teal-500/25 transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Background Layers */}
      <div className="absolute inset-0">
        {/* Gradient base */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#060B18] via-[#0A1628] to-[#0D1117]" />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        {/* Glowing orbs */}
        <div
          className={`absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full transition-all duration-[2000ms] ${
            loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
          }`}
          style={{
            background: 'radial-gradient(circle, rgba(20,184,166,0.15) 0%, transparent 70%)',
          }}
        />
        <div
          className={`absolute -bottom-60 -left-40 w-[500px] h-[500px] rounded-full transition-all duration-[2500ms] delay-300 ${
            loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
          }`}
          style={{
            background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)',
          }}
        />
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full transition-all duration-[3000ms] delay-500 ${
            loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          }`}
          style={{
            background: 'radial-gradient(circle, rgba(20,184,166,0.06) 0%, transparent 60%)',
          }}
        />

        {/* Diagonal light rays */}
        <div
          className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-[0.04]"
          style={{
            background:
              'linear-gradient(135deg, transparent 30%, rgba(20,184,166,0.3) 45%, transparent 55%)',
          }}
        />

        <FloatingParticles />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 w-full min-h-screen flex items-center">
        <div className="w-full max-w-7xl mx-auto px-6 pt-24 pb-16">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column */}
            <div>
              {/* Badge */}
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-teal-500/20 bg-teal-500/5 mb-8 transition-all duration-700 delay-200 ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                </span>
                <span className="text-teal-400 text-xs font-semibold tracking-wider uppercase">
                  AI-Powered Receptionist
                </span>
              </div>

              {/* Headline */}
              <h1 className="font-bold leading-[1.05] mb-6">
                <span
                  className={`block text-5xl lg:text-6xl xl:text-7xl text-white transition-all duration-700 delay-300 ${
                    loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  Never Miss
                </span>
                <span
                  className={`block text-5xl lg:text-6xl xl:text-7xl text-white transition-all duration-700 delay-500 ${
                    loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  A Call{' '}
                  <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
                    Again
                  </span>
                </span>
              </h1>

              {/* Subtitle */}
              <p
                className={`text-lg text-white/60 leading-relaxed max-w-lg mb-10 transition-all duration-700 delay-700 ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                Your AI receptionist handles calls 24/7, books appointments, and delivers
                instant customer support — so you can focus on what matters most.
              </p>

              {/* CTA Buttons */}
              <div
                className={`flex flex-col sm:flex-row gap-4 mb-12 transition-all duration-700 delay-[900ms] ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                <Link
                  to="/start-free-trial"
                  className="group relative px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-semibold rounded-full overflow-hidden flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:shadow-xl hover:shadow-teal-500/25 transition-all duration-300 hover:scale-[1.03]"
                >
                  <span className="relative z-10">Start Free Trial</span>
                  <i className="ri-arrow-right-line text-lg relative z-10 group-hover:translate-x-1 transition-transform"></i>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
                <button
                  onClick={() => setShowVideo(true)}
                  className="group px-8 py-4 border border-white/20 text-white font-semibold rounded-full hover:bg-white/5 hover:border-white/40 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-lg text-teal-400 group-hover:scale-110 transition-transform"></i>
                  Watch Demo
                </button>
              </div>

              {/* Trust Stats */}
              <div
                className={`flex items-center gap-8 transition-all duration-700 delay-[1100ms] ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                <StatsCounter end={10000} label="Businesses" suffix="+" />
                <div className="w-px h-10 bg-white/10" />
                <StatsCounter end={2} label="Avg Response" suffix="s" />
                <div className="w-px h-10 bg-white/10" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-white flex items-center justify-center gap-1">
                    4.9 <i className="ri-star-fill text-amber-400 text-lg"></i>
                  </div>
                  <div className="text-xs text-white/50 mt-1">Rating</div>
                </div>
              </div>
            </div>

            {/* Right Column - Animated Dashboard */}
            <div
              className={`relative transition-all duration-1000 delay-500 ${
                loaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
              }`}
            >
              <div className="relative animate-float">
                {/* Main card */}
                <div className="relative bg-white/[0.04] backdrop-blur-xl rounded-3xl p-6 border border-white/[0.08] shadow-2xl shadow-black/40">
                  {/* Top bar */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400/80" />
                      <div className="w-3 h-3 rounded-full bg-amber-400/80" />
                      <div className="w-3 h-3 rounded-full bg-green-400/80" />
                    </div>
                    <div className="text-white/30 text-xs font-mono">AI Receptionist v3.2</div>
                  </div>

                  {/* Active call simulation */}
                  <div className="bg-white/[0.03] rounded-2xl p-5 mb-4 border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center animate-pulse-slow">
                          <i className="ri-phone-line text-white text-lg"></i>
                        </div>
                        <div>
                          <p className="text-white text-sm font-semibold">Active Call</p>
                          <p className="text-white/40 text-xs">Sarah Johnson • 02:34</p>
                        </div>
                      </div>
                      <div className="px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                        <span className="text-green-400 text-xs font-semibold">Live</span>
                      </div>
                    </div>
                    <AnimatedWaveform />
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/[0.06] text-center">
                      <p className="text-teal-400 text-xl font-bold">147</p>
                      <p className="text-white/40 text-[10px] mt-1">Calls Today</p>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/[0.06] text-center">
                      <p className="text-emerald-400 text-xl font-bold">98%</p>
                      <p className="text-white/40 text-[10px] mt-1">Answered</p>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/[0.06] text-center">
                      <p className="text-teal-300 text-xl font-bold">23</p>
                      <p className="text-white/40 text-[10px] mt-1">Booked</p>
                    </div>
                  </div>

                  {/* Recent activity */}
                  <div className="space-y-2">
                    <p className="text-white/30 text-[10px] font-semibold tracking-wider uppercase mb-2">
                      Recent Activity
                    </p>
                    <NotificationCard
                      icon="ri-calendar-check-line"
                      iconBg="bg-teal-500/20"
                      title="Appointment Booked"
                      subtitle="Dr. Smith — Tomorrow 2:00 PM"
                      delay={1200}
                    />
                    <NotificationCard
                      icon="ri-question-answer-line"
                      iconBg="bg-emerald-500/20"
                      title="FAQ Answered"
                      subtitle="Business hours inquiry resolved"
                      delay={1600}
                    />
                    <NotificationCard
                      icon="ri-phone-line"
                      iconBg="bg-green-500/20"
                      title="Call Transferred"
                      subtitle="VIP client → Sales team"
                      delay={2000}
                    />
                  </div>
                </div>

                {/* Floating badge - top right */}
                <div
                  className={`absolute -top-4 -right-4 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl p-3 shadow-xl shadow-teal-500/20 transition-all duration-700 delay-[1400ms] ${
                    loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-shield-check-line text-white text-lg"></i>
                    <span className="text-white text-xs font-bold whitespace-nowrap">99.9% Uptime</span>
                  </div>
                </div>

                {/* Floating badge - bottom left */}
                <div
                  className={`absolute -bottom-3 -left-3 bg-white/[0.07] backdrop-blur-xl rounded-2xl p-3 border border-white/10 shadow-xl transition-all duration-700 delay-[1800ms] ${
                    loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-white text-xs font-bold">4.9/5 Rating</p>
                      <p className="text-white/40 text-[10px]">2,400+ reviews</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 to-transparent z-10" />
    </section>
  );
}
